// ═══════════════════════════════════════════════════════════════════════
// calc.js — Netrev P&L formula core (single source of truth)
// ═══════════════════════════════════════════════════════════════════════
// Extracted VERBATIM from App.js (no behaviour change) so the P&L math lives
// in ONE place and can be imported anywhere + unit-tested. This is a hard
// INVARIANT (see repo CLAUDE.md): the math is correct & verified — do NOT
// change behaviour here without golden tests + a before/after on-screen P&L
// diff. See calc.test.js.
//
// FORMULA ENGINE — all rates from cfg, verified against spreadsheet.
// Per-row formula correct. Original sheet SUM(E2:E4) was a range typo.
//
import { cpcForPeriod } from "./cpcAlloc";
//
// DEFAULT_CFG — all zeros. If a rate isn't explicitly set, the calculation
// multiplies by 0 (zero commission/card fee/VAT etc). Users must set their
// own rates in Configuration. No silent industry-default fallbacks.
export const DEFAULT_CFG = {
  aggCommission: 0,
  vatRate:       0,
  cogsPct:       0,
  cardFee:       0,
};

// calcDaily — gross is total sales, net is sales after discount
// cfgOrRate can be: number (commission only), or object with {commission, cardFeePct, vatOnFees} (per-channel)
export function calcDaily(gross, net, cpc, cfgOrRate = DEFAULT_CFG, cardFeeOverride) {
  let cfg;
  if (typeof cfgOrRate === "number") {
    cfg = { ...DEFAULT_CFG, aggCommission: cfgOrRate };
    if (typeof cardFeeOverride === "number") cfg.cardFee = cardFeeOverride;
  } else {
    cfg = cfgOrRate || DEFAULT_CFG;
  }
  const commRate = cfg.aggCommission ?? DEFAULT_CFG.aggCommission;
  const cardRate = cfg.cardFee       ?? DEFAULT_CFG.cardFee;
  const vatRate  = cfg.vatRate       ?? DEFAULT_CFG.vatRate;
  const cogsRate = cfg.cogsPct       ?? DEFAULT_CFG.cogsPct;
  const vatOnFees = true; // SEC#25: aggregator fees ALWAYS include VAT on top (toggle removed)

  // Net = sales after discount (or = gross if no discount).
  // GUARD: net can never physically exceed gross (it's post-discount). Clamp to
  // gross so a bad entry (net > gross) can't produce impossible >100% margins.
  // For all valid data (net <= gross) this changes nothing.
  const rawNet       = (typeof net === "number" && net > 0) ? net : gross;
  const netTicket    = (gross > 0) ? Math.min(rawNet, gross) : rawNet;
  // Commission on NET ticket + VAT on commission
  const commissionAmt = netTicket * commRate;
  const vatOnComm     = vatOnFees ? commissionAmt * vatRate : 0;
  // Card fee on NET ticket + VAT on card fee
  const cardFees      = netTicket * cardRate;
  const vatOnCard     = vatOnFees ? cardFees * vatRate : 0;
  // CPC + VAT on CPC
  const cpcSpend      = cpc || 0;
  const vatOnCpc      = vatOnFees ? cpcSpend * vatRate : 0;
  // Total VAT on fees (input VAT — recoverable)
  const totalFeeVat   = vatOnComm + vatOnCard + vatOnCpc;
  // Net receivable = net ticket - commission - CPC - card fee - all VAT on fees
  const netReceivable = netTicket - commissionAmt - cpcSpend - cardFees - totalFeeVat;
  // Output VAT — calculated on net receivable (your actual takings, not net ticket)
  const outputVat     = netReceivable > 0 ? netReceivable * vatRate : 0;
  // COGS on GROSS (food cost ratio)
  const cogs          = gross * cogsRate;
  // True profit = net receivable - COGS (input VAT on fees already deducted from receivable)
  const trueProfit    = netReceivable - cogs;
  const earningAfterVat = netReceivable; // alias kept for compatibility (no separate VAT-deduction step now)
  const netProfitPct   = netReceivable > 0 ? trueProfit / netReceivable : 0;
  const cogsPctOnNet   = netReceivable > 0 ? cogs / netReceivable : 0;
  const pctEarningOnGross = gross > 0 ? netReceivable / gross : 0;
  return {
    commissionAmt:    +commissionAmt.toFixed(2),
    netAfterDiscComm: +netReceivable.toFixed(2),
    cardFees:         +cardFees.toFixed(2),
    vatOnComm:        +vatOnComm.toFixed(2),
    vatOnCard:        +vatOnCard.toFixed(2),
    vatOnCpc:         +vatOnCpc.toFixed(2),
    totalFeeVat:      +totalFeeVat.toFixed(2),
    netReceivable:    +netReceivable.toFixed(2),
    vat:              +outputVat.toFixed(2),  // output VAT on sales (for VAT return)
    earningAfterVat:  +earningAfterVat.toFixed(2),
    cogs:             +cogs.toFixed(2),
    pctEarningOnGross:+pctEarningOnGross.toFixed(4),
    trueProfit:       +trueProfit.toFixed(2),
    netProfitPct:     +Math.max(-9.99,Math.min(9.99,netProfitPct)).toFixed(4),
    cogsPctOnNet:     +cogsPctOnNet.toFixed(4),
  };
}

// Item-level profit calculation for menu items in Price Tool.
// UAE/UK convention: aggregators charge VAT on top of each platform fee.
// All deductions: commission + VAT, card fee + VAT, campaign fee + VAT.
// Card fee base = net ticket (per Careem statement convention).
// Profit = net receivable - cost (the input VAT is recoverable, so not subtracted from profit).
export function calcItem(cost, price, disc, fee, cfgOrRate = DEFAULT_CFG, cardFeeOverride, cap = 0) {
  const cfg = typeof cfgOrRate === "number"
    ? { ...DEFAULT_CFG, aggCommission: cfgOrRate }
    : (cfgOrRate || DEFAULT_CFG);
  const commRate    = cfg.aggCommission ?? DEFAULT_CFG.aggCommission;
  const cardFeeRate = cardFeeOverride ?? cfg.cardFee ?? DEFAULT_CFG.cardFee ?? 0;
  const vatRate     = cfg.vatRate ?? DEFAULT_CFG.vatRate;
  const vatOnFees   = true; // #25: aggregator fees ALWAYS include VAT on top (toggle removed)

  // 1. Net ticket = price after discount. `cap` (currency) limits the discount —
  //    e.g. "50% off up to AED 20": discount = min(price*disc, cap). cap<=0 = none.
  const rawDiscount     = price * (disc || 0);
  const discountApplied = (cap > 0) ? Math.min(rawDiscount, cap) : rawDiscount;
  const netTicket  = +(price - discountApplied).toFixed(3);
  // 2. Commission + VAT on commission
  const commission = +(netTicket * commRate).toFixed(3);
  const vatOnComm  = vatOnFees ? +(commission * vatRate).toFixed(3) : 0;
  // 3. Campaign fee + VAT on campaign fee
  const campFee    = +(fee || 0);
  const vatOnFee   = vatOnFees ? +(campFee * vatRate).toFixed(3) : 0;
  // 4. Card fee on net ticket (per Careem) + VAT on card fee
  const cardFees   = +(netTicket * cardFeeRate).toFixed(3);
  const vatOnCard  = vatOnFees ? +(cardFees * vatRate).toFixed(3) : 0;
  // 5. Net receivable = net ticket - all deductions (fee + their VAT)
  const totalFeeVat = +(vatOnComm + vatOnFee + vatOnCard).toFixed(3);
  const netReceivable = +(netTicket - commission - campFee - cardFees - totalFeeVat).toFixed(3);
  // 6. Output VAT (collected from customer at sale, separate from input VAT on fees)
  const outputVat  = +(netTicket * vatRate).toFixed(3);
  // 7. Profit = net receivable - cost (input VAT on fees is recoverable, so net cost to business = receivable - cost)
  const profit     = +(netReceivable - cost).toFixed(2);

  return { netTicket, discountApplied, commission, cardFees, campFee, vatOnComm, vatOnFee, vatOnCard, totalFeeVat, netReceivable, vat: outputVat, profit };
}

// ── Subscription fee (per-aggregator fixed MONTHLY charge) ────────────
// Deducted at PERIOD level from net receivable (NOT inside calcDaily, which is
// per-entry and must stay untouched). PRORATED over the SELECTED period using
// each calendar month's ACTUAL days (owner request): a full June (or any full
// 31-day month) = the whole fee, and a cross-month range adds each month's real
// fraction. Falls back to days ÷ 30.4375 only when no date window is available
// (e.g. the "All" range).
//   • "All channels" view sums every channel's monthly fee.
//   • VAT on the subscription fee uses the workspace VAT rate, same treatment as
//     commission/card-fee VAT (recoverable input VAT), also reducing net receivable.

// Month-aware month-count for an inclusive [fromYMD,toYMD] date range: Σ over
// each calendar month it touches of (days of the range in that month ÷ that
// month's ACTUAL days). A full calendar month → 1.0 regardless of 28/30/31 days.
export function monthsForSpan(fromYMD, toYMD) {
  if (!fromYMD || !toYMD) return 0;
  const start = new Date(`${fromYMD}T00:00:00`);
  const end   = new Date(`${toYMD}T00:00:00`);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return 0;
  let months = 0, y = start.getFullYear(), m = start.getMonth();
  const endY = end.getFullYear(), endM = end.getMonth();
  while (y < endY || (y === endY && m <= endM)) {
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const segStart = Math.max(new Date(y, m, 1).getTime(), start.getTime());
    const segEnd   = Math.min(new Date(y, m + 1, 0).getTime(), end.getTime());
    months += (Math.round((segEnd - segStart) / 86400000) + 1) / daysInMonth;
    m += 1; if (m > 11) { m = 0; y += 1; }
  }
  return months;
}
// Fallback when no date window is known (e.g. the "All" range): average-month
// day-proration (365.25 ÷ 12 = 30.4375 days/month).
export function subscriptionMonthsForPeriod(periodDays) {
  if (!periodDays || periodDays <= 0) return 0;
  return periodDays / 30.4375;
}
export function computeSubscriptionForPeriod(channelsInScope, periodDays, vatRate, fromYMD, toYMD) {
  const monthlySum = (channelsInScope || []).reduce((s, c) => s + (c.subscriptionFee || 0), 0);
  const months = (fromYMD && toYMD) ? monthsForSpan(fromYMD, toYMD) : subscriptionMonthsForPeriod(periodDays);
  const fee = +(monthlySum * months).toFixed(2);
  const vat = +(fee * (vatRate || 0)).toFixed(2); // VAT on subscription, like other fee VAT
  return { fee, vat, total: +(fee + vat).toFixed(2), months, monthlySum };
}

// ── applyPeriodAdjustments — SINGLE source of truth for period-level P&L ──
// Takes a totals object `t` already holding the per-row calcDaily SUMS
//   {gross,net,commission,vatOnComm,cardFees,vatOnCard,cpc,vatOnCpc,campaignFee,netReceivable,cogs}
// and applies the period-level adjustments on top: full-period CPC top-up,
// (optional) subscription, recoverable fee-VAT total, output VAT, earnings
// after VAT, and TRUE PROFIT. Every portal/report surface calls this so the
// ladder is identical everywhere. calcDaily / calcItem (per-ENTRY math) are
// NOT touched — only this period roll-up is centralised.
export function applyPeriodAdjustments(t, opts) {
  const { allocs = [], winFrom = null, winTo = null, vatRate = 0,
          includeSubscription = false, subChannels = [], subPeriodDays = 30 } = opts || {};
  const r2 = (n) => +(Number(n) || 0).toFixed(2);
  // 1. CPC full-period top-up — full committed spend incl. in-period days with NO sales entry
  const periodCpc = cpcForPeriod(allocs, winFrom, winTo);
  const extraCpc  = periodCpc - (t.cpc || 0);
  t.netReceivable = r2(t.netReceivable - extraCpc * (1 + vatRate));
  t.cpc           = r2(periodCpc);
  t.vatOnCpc      = r2(periodCpc * vatRate);
  // 2. Subscription — Overview & Insight ONLY (never Daily Log / report)
  let subFee = 0, subVat = 0;
  if (includeSubscription) {
    const sub = computeSubscriptionForPeriod(subChannels, subPeriodDays, vatRate, winFrom, winTo);
    subFee = sub.fee; subVat = sub.vat;
    t.netReceivable = r2(t.netReceivable - sub.total);
  }
  t.subscriptionFee = subFee;
  t.subscriptionVat = subVat;
  // 3. Recoverable input VAT on fees (informational total / donut slice)
  t.totalFeeVat = r2((t.vatOnComm || 0) + (t.vatOnCard || 0) + (t.vatOnCpc || 0) + subVat);
  // 4. Output VAT — on the FINAL net receivable, clamped at 0
  t.vat  = r2(Math.max(0, t.netReceivable) * vatRate);
  // 5. Earnings after VAT = net receivable − output VAT   (VAT IS DEDUCTED — the owner's rule)
  t.eav  = r2(t.netReceivable - t.vat);
  // 6. TRUE PROFIT = earnings after VAT − COGS  (so VAT flows through to profit)
  t.profit = r2(t.eav - t.cogs);
  return t;
}

// Break-even analysis from history rows + fixed costs
export function calcBreakEvenFromRows(rows, fixedCosts, daysInPeriod) {
  const totalFixed   = (fixedCosts?.rent||0)+(fixedCosts?.wages||0)+(fixedCosts?.utilities||0)+(fixedCosts?.other||0);
  const totalGross   = rows.reduce((a,r)=>a+(r.gross||0),0);
  const totalProfit  = rows.reduce((a,r)=>a+(r.trueProfit||0),0);
  const totalEAV     = rows.reduce((a,r)=>a+(r.earningAfterVat||0),0);
  const totalCOGS    = rows.reduce((a,r)=>a+(r.cogs||0),0);
  // Real contribution = earning after VAT minus COGS (no estimated 0.8/0.25 hack)
  const totalContribution = totalEAV - totalCOGS;
  const days = Math.max(1, daysInPeriod);
  const avgDailyGross = totalGross / days;
  const dailyFixed = totalFixed / 30.4;
  const contribPct = totalGross > 0 ? totalContribution / totalGross : 0;
  const breakEvenDailyGross = contribPct > 0 ? dailyFixed / contribPct : 0;
  const coveragePercent = breakEvenDailyGross > 0 && avgDailyGross > 0
    ? Math.min((avgDailyGross / breakEvenDailyGross) * 100, 200)
    : 0;
  // Scale fixed costs to actual period length for true profit-after-fixed
  const periodFixedCosts = (totalFixed / 30.4) * days;
  return {
    totalFixed,
    dailyFixed: +dailyFixed.toFixed(2),
    breakEvenDailyGross: +breakEvenDailyGross.toFixed(2),
    avgDailyGross: +avgDailyGross.toFixed(2),
    contribPct: +contribPct.toFixed(4),
    coveragePercent: +coveragePercent.toFixed(0),
    profitAfterFixed: +(totalProfit - periodFixedCosts).toFixed(2),
    daysInPeriod: days,
  };
}
